import {
  type Offer,
  type OfferCard,
  offerCards,
  type PriceRange,
  priceRanges,
  type ShopLocation,
  type StoreId,
} from "@larder/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import { fetchPrices, type PriceSource } from "./prices";

/**
 * Offers for one thing, near one household.
 *
 * Fires on open. It used to say nothing while it waited — the screen is
 * complete without prices (ADR 0024), so "no offers yet" and "offers" were
 * drawn as the same screen with a row of cards added. That was wrong in one
 * specific way: cards that appear from nowhere two seconds after the screen,
 * or never, look like a feature that does not exist rather than one that is
 * busy or out of budget. So the wait is now a shape (`status`), and an empty
 * answer carries where it came from (`source`) so the screen can say why.
 *
 * Nothing is fetched at all until the household HAS a city, which keeps the
 * 250-a-month budget spent on households that asked for the feature.
 */
export interface ShopPrices {
  /** `idle` when there is no city to ask for; `loading` while the lookup is out. */
  status: "idle" | "loading" | "ready";
  /** Where the answer came from. `none` means nobody could be asked. */
  source: PriceSource;
  /** Every offer worth showing, ranked, each with somewhere to go. */
  cards: readonly OfferCard[];
  /** What each shop's listings span, for the browse buttons. */
  spans: ReadonlyMap<StoreId, PriceRange>;
  /** When the underlying lookup ran. 0 when there is nothing. */
  at: number;
}

const EMPTY = { cards: [], spans: new Map() } as const;

export function useShopPrices(
  query: string,
  where: ShopLocation | null | undefined,
): ShopPrices {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [at, setAt] = useState(0);
  const [status, setStatus] = useState<ShopPrices["status"]>("idle");
  const [source, setSource] = useState<PriceSource>("none");
  /**
   * Guards the answer against arriving for a line the user has already left.
   * Switching between two cart lines quickly would otherwise paint the first
   * one's offers onto the second one's screen.
   */
  const wanted = useRef("");

  useEffect(() => {
    const q = query.trim();
    const key = `${q}|${where?.canonicalName ?? ""}`;
    wanted.current = key;
    setOffers([]);
    setAt(0);
    setSource("none");
    if (!q || !where?.canonicalName) {
      setStatus("idle");
      return;
    }

    let live = true;
    setStatus("loading");
    fetchPrices(q, where).then((answer) => {
      if (!live || wanted.current !== key) return;
      setOffers(answer.offers);
      setAt(answer.at);
      setSource(answer.source);
      setStatus("ready");
    });
    return () => {
      live = false;
    };
  }, [query, where]);

  return useMemo(() => {
    if (offers.length === 0) return { ...EMPTY, at, status, source };
    return {
      cards: offerCards(offers, query),
      spans: priceRanges(offers),
      at,
      status,
      source,
    };
  }, [offers, at, query, status, source]);
}
